import { createContext, useContext } from 'react';

import type { DraftSource } from './draftReducer';
import type { WorkflowDraft } from './types';

/**
 * The context and its hook, separated from the provider component.
 *
 * Not a stylistic split: Fast Refresh only preserves state for a module whose
 * exports are all components, so keeping `useDraft` beside `DraftProvider`
 * would remount the builder — discarding the draft — on every edit to either.
 * Losing unsaved work on save-while-typing is exactly the failure this feature
 * exists to prevent, so the lint rule is load-bearing here.
 */

export type DraftContextValue = {
  draft: WorkflowDraft;
  canUndo: boolean;
  canRedo: boolean;

  /** Apply an edit and record one history entry. */
  edit: (change: (draft: WorkflowDraft) => WorkflowDraft) => void;

  /**
   * Apply an edit *without* recording history — the intermediate frames of a
   * gesture. The gesture's end calls `edit` once, so a single undo returns to
   * where the gesture started rather than to three pixels ago (research D2).
   */
  editTransient: (change: (draft: WorkflowDraft) => WorkflowDraft) => void;

  undo: () => void;
  redo: () => void;

  /** Adopt the document a successful save returned (FR-051). */
  saveSucceeded: (view: DraftSource) => void;
};

export const DraftContext = createContext<DraftContextValue | null>(null);

/**
 * The draft and its edit operations.
 *
 * Throws outside a provider rather than handing back an empty default: a
 * component that reads the draft can do nothing sensible without one, and a
 * silent empty draft would render as an empty workflow — which looks like data
 * loss rather than a wiring mistake.
 */
export function useDraft(): DraftContextValue {
  const value = useContext(DraftContext);
  if (!value) {
    throw new Error('useDraft must be used inside a DraftProvider.');
  }
  return value;
}
